<script setup lang="ts">
const props = defineProps({
  item: {
    type: Object as PropType<Item>,
    required: true,
  },
});
const { getMerchantAttributes } = useLuxuryDesign();
const collectibleDesign = computed(() =>
  getMerchantAttributes(props.item.merchantId)
);

const isLuxuryDesign =
  collectibleDesign.value["data-merchant-design"] === "luxury";

const isHovered = ref<boolean>(false);
const isMobile = useMediaQuery("(max-width: 767px)");
const isNextTimePassed = ref<boolean>(true);
const counter = ref(100);
const { lockedTime, nextTime } = toRefs(props.item);
let counterId: ReturnType<typeof setInterval>;

const showProgressBar = ref(false);

const checkIfNextTimePassed = () => {
  let totalTime = 0;
  showProgressBar.value = false;
  if (nextTime.value && lockedTime.value) {
    const start = new Date(lockedTime.value).getTime();
    const end = new Date(nextTime.value).getTime();
    totalTime = end - start;

    if (Date.now() > end) {
      isNextTimePassed.value = true;
    } else if (totalTime > 0) {
      isNextTimePassed.value = false;
      showProgressBar.value = true;
      const calculateAndUpdateCounter = () => {
        const now = Date.now();
        const timeLeft = end - now;
        counter.value = Math.max(0, Math.round((timeLeft * 100) / totalTime));

        if (timeLeft <= 0) {
          isNextTimePassed.value = true;
          showProgressBar.value = false;
          clearInterval(counterId);
        }
      };
      calculateAndUpdateCounter();
      counterId = setInterval(calculateAndUpdateCounter, 1000);
    }
  }
};

const isItemComingSoon = computed(() => {
  if (props.item.start) {
    const startDate = new Date(props.item.start);
    const now = new Date();
    return startDate > now;
  }
  return false;
});

const getStatusText = computed(() => {
  if (isItemComingSoon.value) {
    return "main.cardCollectible.startingAt";
  }
  if (props.item.number === 0) {
    return "main.cardCollectible.lastSold";
  } else if (isNextTimePassed.value) {
    return "main.cardCollectible.buyNow";
  } else {
    return "main.cardCollectible.buySoon";
  }
});

onMounted(() => {
  checkIfNextTimePassed();
});

onUnmounted(() => {
  clearInterval(counterId);
});

watch(counter, (value) => {
  if (value <= 0) {
    isNextTimePassed.value = true;
    clearInterval(counterId);
  }
});
</script>
<template>
  <div
    v-bind="collectibleDesign"
    class="relative grid grid-rows-[1fr,1fr,1fr,1fr, 1fr] border rounded-lg border-primary-900 will-change-transform transition ease-in-out duration-300 overflow-visible collectible-card"
    :class="{ '-translate-y-1': isHovered }"
    @mouseover="isHovered = true"
    @mouseleave="isHovered = false">
    <div class="relative overflow-hidden">
      <CardImage :src="getPortraitItemImage(item.imageId)" />
      <div
        v-if="
          item.number != null && item.number !== undefined && !isItemComingSoon
        "
        class="absolute bottom-4 right-0 text-xs text-neutral-200 text-center font-bold w-fit px-4 py-1 rounded-tl-2xl rounded-bl z-20 collectible-card__supply"
        :class="
          item.number <= item.lowSupplyThreshold &&
          item.totalCopies &&
          item.totalCopies !== item.number
            ? 'bg-error-300'
            : 'bg-neutral-500'
        ">
        <template v-if="item.number === 0">
          {{ $t("main.cardCollectible.soldOut") }}
        </template>
        <template
          v-else-if="
            item.number <= item.lowSupplyThreshold &&
            item.totalCopies &&
            item.totalCopies !== item.number
          ">
          {{
            $t("main.cardCollections.unitsLeft", {
              units: item.number,
              copies: item.totalCopies,
            })
          }}
        </template>
        <template v-else>
          {{ $t("main.cardCollections.supply") }}
          {{ item.number }} <span class="font-[Roboto] font-black">/</span>
          {{ item.totalCopies }}
        </template>
      </div>
      <div v-if="!isNextTimePassed" class="absolute bottom-2 left-2 z-20">
        <NuxtIcon
          name="mdi:lock-outline"
          alt="lock"
          class="w-9 h-9 text-neutral-300" />
      </div>
    </div>
    <div
      v-if="!isLuxuryDesign"
      class="w-full grid grid-rows-[4px_2px] z-10 collectible-card__rarity">
      <div :class="getRarityColor(item.rarity!, 'bg')"></div>
      <div class="opacity-80" :class="getRarityColor(item.rarity!, 'bg')"></div>
    </div>
    <div class="px-2 py-3.5 text-center truncate merchant-card__surface">
      <h1
        class="text-neutral-200 text-lg font-bold truncate merchant-heading"
        :class="{ 'merchant-heading--highlight': item.id === 'default-jewel' }">
        {{ $t(`${item.translationId}.title`) }}
        {{ $t(`${item.translationId}.subtitle`) }}
      </h1>
    </div>
    <template v-if="!isItemComingSoon">
      <div
        class="pt-3.5 pb-2.5 border-t border-primary-900 px-2 py-2.5 flex justify-between merchant-card__surface">
        <p class="text-secondary-200 font-semibold text-xs pt-1">
          {{ $t(getStatusText) }}
        </p>
        <div class="flex flex-col items-end">
          <div class="flex gap-2">
            <BaseIcon name="GFAL_token" alt="$GFAL" class="w-4" />
            <p v-if="item.price?.GFAL" class="text-neutral-200 font-bold">
              {{ roundNum(item.price.GFAL) }}
            </p>
          </div>
          <p
            v-if="item.price?.USD"
            class="text-neutral-300 text-xs font-semibold">
            {{ "$" + roundNum(item.price.USD) }}
          </p>
        </div>
      </div>
      <div
        v-if="
          (item.number != null && item.number !== undefined) ||
          (item.level && item.level > 0)
        "
        class="border-t border-primary-900 px-2 py-2 flex items-center gap-4 text-[10px] font-semibold merchant-card__surface">
        <div
          v-if="item.number != null && item.number !== undefined"
          class="relative flex gap-1"
          :class="isMobile ? 'flex-col items-start gap-0.5' : 'items-center'">
          <template v-if="isMobile">
            <span class="text-neutral-200">{{ item.number }}</span>
            <span class="text-[10px] text-neutral-400 leading-none">
              {{ $t("main.cardDetail.tooltip.mintNumberTitle") }}
            </span>
          </template>
          <template v-else>
            <span class="text-neutral-400">
              {{ $t("main.cardDetail.tooltip.mintNumberTitle") }}
            </span>
            <span class="peer text-neutral-200 cursor-help">
              #{{ item.number }}
            </span>
            <CommonTooltip position="top">
              <template #tooltip-text>
                <div class="max-w-xs sm:max-w-none whitespace-normal sm:whitespace-nowrap text-xs text-neutral-800 leading-relaxed">
                  <p class="font-semibold mb-1">
                    {{ $t("main.cardDetail.tooltip.mintNumberTitle") }}
                  </p>
                  <p>
                    {{ $t("main.cardDetail.tooltip.mintNumber") }}
                  </p>
                </div>
              </template>
            </CommonTooltip>
          </template>
        </div>
        <div
          v-if="item.level && item.level > 0"
          class="relative flex gap-1"
          :class="isMobile ? 'flex-col items-start gap-0.5' : 'items-center'">
          <!-- <BaseIconHexagon class="w-4 h-4 text-neutral-200" /> -->
          <template v-if="isMobile">
            <span class="text-neutral-200">{{ item.level }}</span>
            <span class="text-[10px] text-neutral-400 leading-none">
              {{ $t("main.cardDetail.tooltip.itemValueTitle") }}
            </span>
          </template>
          <template v-else>
            <span class="text-neutral-400">
              {{ $t("main.cardDetail.tooltip.itemValueTitle") }}
            </span>
            <span class="peer text-neutral-200 cursor-help">
              {{ item.level }}
            </span>
            <CommonTooltip position="top">
              <template #tooltip-text>
                <div class="max-w-xs sm:max-w-none whitespace-normal sm:whitespace-nowrap text-xs text-neutral-800 leading-relaxed">
                  <p class="font-semibold mb-1">
                    {{ $t("main.cardDetail.tooltip.itemValueTitle") }}
                  </p>
                  <p>
                    {{ $t("main.cardDetail.tooltip.itemValue") }}
                  </p>
                </div>
              </template>
            </CommonTooltip>
          </template>
        </div>
      </div>
    </template>
    <div
      v-else
      class="pt-5 pb-5 border-t border-primary-900 px-2 py-2.5 flex justify-center items-center merchant-card__surface">
      <p class="text-secondary-200 font-semibold text-md text-center w-full">
        {{ $t("main.home.comingSoon") }}
      </p>
    </div>
    <div
      class="relative rounded-b-lg border-t border-primary-900 merchant-card__surface">
      <Transition mode="out-in">
        <div
          v-if="item.start && isItemComingSoon"
          class="text-xs flex items-center gap-1 py-3.5 px-2 overflow-hidden rounded-b-lg">
          <NuxtIcon name="mdi:clock-outline" class="text-neutral-200 w-6 h-6" />
          <p class="text-secondary-200">{{ $t(`main.availableOn`) }}</p>
          <p class="text-neutral-200 font-semibold">
            {{
              new Date(item.start).toLocaleDateString($i18n.locale, {
                month: "long",
                day: "numeric",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })
            }}
          </p>
        </div>
        <div
          v-else-if="isNextTimePassed && item.number !== 1 && item.number !== 0"
          class="relative flex flex-col justify-center py-3.5 px-2 overflow-hidden rounded-b-lg collectible-card__next-price">
          <div
            class="z-10 flex items-center justify-between gap-1 text-xs text-neutral-300 font-semibold">
            <p class="">
              {{ $t("main.cardCollectible.nextCollectible") }}
            </p>
            <div class="flex items-center gap-1">
              <p class="font-bold">
                ${{
                  item.nextPrice?.USD
                    ? roundNum(item.nextPrice.USD)
                    : roundNum(item.price!.GFAL)
                }}
              </p>
              <div class="flex items-center">
                <NuxtIcon
                  name="mdi:arrow-up-bold-circle-outline"
                  alt="icon of red arrow pointing up"
                  class="w-6 h-6 collectible-card__next-price-icon" />
                <p
                  v-if="item.nextPrice"
                  class="collectible-card__next-price-text">
                  +{{
                    calculateDiscount(
                      item.price!.GFAL,
                      item.nextPrice?.GFAL,
                      2
                    )
                  }}%
                </p>
              </div>
            </div>
          </div>
        </div>
        <div
          v-else-if="item.number === 1"
          class="relative flex flex-col justify-center py-3.5 px-2 overflow-hidden rounded-b-lg">
          <p class="text-rarity-exotic text-center">
            {{ $t("main.cardCollectible.lastCollectible") }}
          </p>
        </div>
        <div
          v-else-if="item.number === 0"
          class="relative flex flex-col justify-center py-3.5 px-2 overflow-hidden rounded-b-lg">
          <div class="h-6"></div>
        </div>
        <div v-else>
          <div class="flex items-center justify-between text-xs px-2 py-3.5">
            <p class="text-neutral-200">
              {{ $t("main.cardCollectible.availableIn") }}
            </p>
            <CommonCountdown
              v-if="item.nextTime"
              :datetime="item.nextTime"
              class="text-neutral-200 font-bold"
              @countdownEnded="isNextTimePassed = true" />
          </div>
          <div
            class="mt-auto w-full bg-secondary-200 rounded-b-lg overflow-hidden">
            <div
              v-if="showProgressBar && counter !== 0"
              class="h-1 bar bg-primary-500 rounded-bl-lg transition-all ease-in-out duration-1000 will-change-transform"></div>
          </div>
        </div>
      </Transition>
    </div>
  </div>
</template>

<style scoped>
.bar {
  width: calc(100% / 100 * (v-bind("counter")));
}

.v-enter-active,
.v-leave-active {
  transition: opacity 0.3s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}
</style>
